import React, { useState } from "react";
import { useAuth } from "./AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { toast } from "sonner";
import {
  FileText,
  Shield,
  Cookie,
  Send,
  CheckCircle2,
  Sparkles,
  Globe2,
  Layers,
  ScanSearch,
  Mail,
  User,
} from "lucide-react";

/* ============================================================
   DNH — Footer Modals
   About · Legal (Terms / Privacy / Cookies) · Support
   Editorial design language. Paper-cream surface, ink/cream text,
   brand orange accent. NO neon, NO glow.
   ============================================================ */

/* ---------------------- Shared header ---------------------- */
const ModalHeader = ({ eyebrow, title, kicker }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: "var(--crimson)" }}
        aria-hidden
      />
      <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--muted)]">
        {eyebrow}
      </span>
    </div>
    <DialogTitle
      asChild
      className="font-serif text-[28px] sm:text-[32px] leading-[1.1] tracking-[-0.012em] text-[var(--ink)]"
    >
      <h2>{title}</h2>
    </DialogTitle>
    {kicker && (
      <DialogDescription className="font-sans text-[14.5px] leading-[1.6] text-[var(--ink-2)] mt-3 max-w-[58ch]">
        {kicker}
      </DialogDescription>
    )}
  </div>
);

/* ============================================================
   ABOUT
   ============================================================ */
export const AboutModal = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      data-testid="about-modal"
      className="bg-[var(--paper)] text-[var(--ink)] border border-[var(--hairline-strong,#D8D2C6)] rounded-[18px] sm:max-w-[760px] max-h-[88vh] overflow-y-auto p-0"
    >
      <DialogHeader className="p-7 sm:p-9 pb-0 text-left">
        <ModalHeader
          eyebrow="About · Daily News Highlights"
          title="We don't make news. We make sense of it."
          kicker="DNH brings together source reporting, story highlights and AI-assisted analysis. Coverage varies by place and publisher; publication dates and source links help you assess what you read."
        />
      </DialogHeader>

      <div className="px-7 sm:px-9 pb-9 mt-7">
        {/* Manifesto pull-quote */}
        <blockquote
          className="font-serif text-[19px] sm:text-[20px] leading-[1.45] text-[var(--ink)] border-l-2 pl-5 py-1 italic"
          style={{ borderColor: "var(--crimson)" }}
          data-testid="about-manifesto"
        >
          A newsroom doesn't write everything. A reader doesn't read everything.
          Somewhere between the two sits the meaning of the day — and that's the
          space DNH was built to occupy.
        </blockquote>

        {/* Three editorial pillars */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[var(--hairline)] rounded-[14px] overflow-hidden border border-[var(--hairline)]">
          {[
            {
              icon: ScanSearch,
              eyebrow: "01 · Curated",
              title: "Source-led reporting",
              body: "Story previews come from publisher feeds. AI assists with summaries and analysis, which may contain errors and should be checked against the linked sources.",
            },
            {
              icon: Layers,
              eyebrow: "02 · Synthesised",
              title: "Confidence on every story",
              body: "Each brief carries a confidence score reflecting source diversity, corroboration and update velocity — so you know what you're reading and why.",
            },
            {
              icon: Globe2,
              eyebrow: "03 · Global",
              title: "Global perspective",
              body: "Coverage follows the available reporting. Scheduled feed checks do not guarantee new stories for every country or region.",
            },
          ].map((p) => (
            <div
              key={p.eyebrow}
              className="bg-[var(--paper)] p-5 sm:p-6 flex flex-col gap-3"
            >
              <span
                className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[var(--ink)] text-[var(--paper)]"
                aria-hidden
              >
                <p.icon size={15} strokeWidth={1.75} />
              </span>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--crimson)]">
                {p.eyebrow}
              </div>
              <div className="font-serif text-[17px] leading-[1.25] text-[var(--ink)]">
                {p.title}
              </div>
              <p className="font-sans text-[13px] leading-[1.55] text-[var(--ink-2)]">
                {p.body}
              </p>
            </div>
          ))}
        </div>

        {/* What we are / aren't — clarifying box */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="rounded-[12px] border border-[var(--hairline)] bg-[var(--paper)] p-5">
            <div className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-[var(--ink)] mb-2">
              What DNH is
            </div>
            <ul className="space-y-2 font-sans text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
              <li>· A curation & synthesis layer over the world's news</li>
              <li>· An editorial product, not a feed</li>
              <li>· A confidence-first reading experience</li>
              <li>· Built for decision-makers, builders and the deeply curious</li>
            </ul>
          </div>
          <div className="rounded-[12px] border border-[var(--hairline)] bg-[var(--surface-2)] p-5">
            <div className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-[var(--muted)] mb-2">
              What DNH is not
            </div>
            <ul className="space-y-2 font-sans text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
              <li>· An original news publisher</li>
              <li>· A guarantee that AI-assisted analysis is error-free</li>
              <li>· A social media feed</li>
              <li>· A substitute for primary reporting — we credit it</li>
            </ul>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

/* ============================================================
   LEGAL — Terms / Privacy / Cookies
   Defensively written. Plain-language but firm.
   ============================================================ */
const LegalSection = ({ heading, children }) => (
  <section className="mb-6">
    <h3 className="font-serif text-[18px] leading-[1.25] text-[var(--ink)] mb-2">
      {heading}
    </h3>
    <div className="font-sans text-[13.5px] leading-[1.65] text-[var(--ink-2)] space-y-2">
      {children}
    </div>
  </section>
);

export const LegalModal = ({ open, onOpenChange, initialTab = "terms" }) => {
  const [tab, setTab] = useState(initialTab);
  // Sync if parent forces a new initial tab while opening
  React.useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="legal-modal"
        className="bg-[var(--paper)] text-[var(--ink)] border border-[var(--hairline-strong,#D8D2C6)] rounded-[18px] sm:max-w-[820px] max-h-[88vh] overflow-y-auto p-0"
      >
        <DialogHeader className="p-7 sm:p-9 pb-0 text-left">
          <ModalHeader
            eyebrow="Legal · Daily News Highlights"
            title="Terms, privacy & cookies"
            kicker="Read these carefully. By continuing to use Daily News Highlights you agree to them. If you do not agree with any part of this policy, please stop using the service immediately and delete your account."
          />
        </DialogHeader>

        <div className="px-7 sm:px-9 pb-9 mt-6">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList
              className="grid grid-cols-3 bg-[var(--surface-2)] rounded-[10px] p-1 mb-6"
              data-testid="legal-tabs"
            >
              <TabsTrigger
                value="terms"
                data-testid="legal-tab-terms"
                className="data-[state=active]:bg-[var(--paper)] data-[state=active]:shadow-sm data-[state=active]:text-[var(--ink)] text-[var(--ink-2)] font-mono text-[10.5px] tracking-[0.18em] uppercase rounded-[8px]"
              >
                <FileText size={13} className="mr-1.5" strokeWidth={1.75} />
                Terms
              </TabsTrigger>
              <TabsTrigger
                value="privacy"
                data-testid="legal-tab-privacy"
                className="data-[state=active]:bg-[var(--paper)] data-[state=active]:shadow-sm data-[state=active]:text-[var(--ink)] text-[var(--ink-2)] font-mono text-[10.5px] tracking-[0.18em] uppercase rounded-[8px]"
              >
                <Shield size={13} className="mr-1.5" strokeWidth={1.75} />
                Privacy
              </TabsTrigger>
              <TabsTrigger
                value="cookies"
                data-testid="legal-tab-cookies"
                className="data-[state=active]:bg-[var(--paper)] data-[state=active]:shadow-sm data-[state=active]:text-[var(--ink)] text-[var(--ink-2)] font-mono text-[10.5px] tracking-[0.18em] uppercase rounded-[8px]"
              >
                <Cookie size={13} className="mr-1.5" strokeWidth={1.75} />
                Cookies
              </TabsTrigger>
            </TabsList>

            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)] mb-4">
              Policy review required before launch · Product description updated 12 October 2026
            </div>

            {/* ----------------- TERMS ----------------- */}
            <TabsContent value="terms" className="mt-0">
              <LegalSection heading="1 · What DNH is — and what it isn't">
                <p>
                  Daily News Highlights ("DNH", "we", "us") is a curation,
                  structuring and synthesis layer over the world's news. <strong>We do not produce, originate, generate or fabricate news.</strong>{" "}
                  All factual material referenced in our briefs originates from
                  third-party publishers, wires and credentialed sources, which
                  are attributed on every brief.
                </p>
                <p>
                  Our editorial role is limited to selecting, organising,
                  summarising and contextualising material that already exists
                  in the public record. Any summary, framing or commentary is
                  editorial interpretation and should not be relied upon as
                  primary reporting or as advice of any kind.
                </p>
              </LegalSection>

              <LegalSection heading="2 · Use of the service">
                <p>
                  You may use DNH for personal, non-commercial reading. You may
                  not scrape, mirror, retrain models on, resell or republish
                  DNH content without prior written permission. Automated
                  access is prohibited except via routes we explicitly publish.
                </p>
                <p>
                  We may modify, suspend or terminate any part of the service —
                  or your access to it — at any time, for any reason, with or
                  without notice, and without liability.
                </p>
              </LegalSection>

              <LegalSection heading="3 · No liability for content or decisions">
                <p>
                  News is fast, imperfect and frequently revised. DNH provides
                  briefs <strong>"as is" and "as available"</strong>, without
                  warranty of any kind — including warranties of accuracy,
                  completeness, timeliness, non-infringement, fitness for a
                  particular purpose or merchantability.
                </p>
                <p>
                  You agree that <strong>any decision you make based on a DNH brief is your decision alone</strong>. DNH, its operators, contributors, vendors and
                  affiliates shall not be liable for any direct, indirect,
                  incidental, special, consequential or punitive damages
                  arising from your use of, or inability to use, the service —
                  including financial loss, missed opportunities, business
                  interruption, reputational harm or loss of data.
                </p>
                <p>
                  Confidence scores are editorial indicators of source
                  triangulation — not guarantees of truth.
                </p>
              </LegalSection>

              <LegalSection heading="4 · Your right to leave">
                <p>
                  If at any time you do not agree with these terms, do not wish
                  to continue reading, or are dissatisfied with the service for
                  any reason, your sole remedy is to{" "}
                  <strong>stop using DNH and delete your account</strong>.
                  Account deletion is permanent and is available from your
                  account settings or by contacting Support.
                </p>
              </LegalSection>

              <LegalSection heading="5 · Third-party content & links">
                <p>
                  DNH credits and links to third-party publishers. We do not
                  control, endorse or assume responsibility for any third-party
                  content, advertising, products or services.
                </p>
              </LegalSection>

              <LegalSection heading="6 · Governing law & changes">
                <p>
                  These terms are governed by the laws applicable at our
                  primary place of operation. We may update these terms at any
                  time; continued use of the service after changes constitutes
                  acceptance of the revised terms.
                </p>
              </LegalSection>
            </TabsContent>

            {/* ----------------- PRIVACY ----------------- */}
            <TabsContent value="privacy" className="mt-0">
              <LegalSection heading="1 · What we collect">
                <p>
                  We collect only what is necessary to run the service: account
                  details you provide (name, email), reading preferences you
                  set, and standard technical signals (device type, language,
                  approximate region, page-load diagnostics).
                </p>
                <p>
                  We do not knowingly collect data from anyone under the age
                  required by local law in your jurisdiction.
                </p>
              </LegalSection>

              <LegalSection heading="2 · How we use it">
                <p>
                  Your data is used solely to (a) deliver the service, (b)
                  personalise relevance of briefs, (c) prevent abuse and
                  protect platform integrity, and (d) respond to support
                  requests. We do not sell your personal data.
                </p>
              </LegalSection>

              <LegalSection heading="3 · Editorial independence">
                <p>
                  Personalisation never overrides editorial confidence. The
                  same brief, with the same sources and the same confidence
                  score, is shown to every reader — only the order of
                  recommendations may adapt.
                </p>
              </LegalSection>

              <LegalSection heading="4 · Your rights & deletion">
                <p>
                  You may request a copy of your data, correction of
                  inaccuracies, or full deletion of your account and
                  associated data at any time, via Support or account
                  settings. Deletion is permanent; we cannot restore deleted
                  accounts.
                </p>
                <p>
                  Some minimal records (e.g. abuse reports, tax records) may
                  be retained where required by law.
                </p>
              </LegalSection>

              <LegalSection heading="5 · Security & limitations">
                <p>
                  We apply reasonable, industry-standard safeguards. However,
                  no online service can guarantee absolute security. You use
                  DNH at your own risk and acknowledge that DNH is not liable
                  for unauthorised access to your account caused by
                  compromised credentials or third-party breaches outside our
                  reasonable control.
                </p>
              </LegalSection>
            </TabsContent>

            {/* ----------------- COOKIES ----------------- */}
            <TabsContent value="cookies" className="mt-0">
              <LegalSection heading="1 · What we use cookies for">
                <p>
                  DNH uses a small number of first-party cookies and
                  equivalent storage technologies (localStorage) to keep you
                  signed in, remember your reading preferences, and measure
                  basic, anonymised performance of the service.
                </p>
              </LegalSection>

              <LegalSection heading="2 · Categories">
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>
                    <strong>Strictly necessary —</strong> session, security,
                    fraud prevention. These cannot be turned off.
                  </li>
                  <li>
                    <strong>Preference —</strong> language, region, dark/light
                    mode, font scale.
                  </li>
                  <li>
                    <strong>Analytics —</strong> aggregated, anonymised reads
                    and engagement to improve editorial quality.
                  </li>
                </ul>
              </LegalSection>

              <LegalSection heading="3 · Third-party content">
                <p>
                  Where briefs embed third-party media (videos, audio,
                  publisher widgets), those providers may set their own
                  cookies under their own policies. DNH is not responsible for
                  third-party cookies.
                </p>
              </LegalSection>

              <LegalSection heading="4 · Your control">
                <p>
                  You can clear cookies or block them at the browser level at
                  any time. Doing so may sign you out and disable
                  preference-based features. You may also delete your account
                  at any time, which removes all associated identifiers from
                  the service.
                </p>
              </LegalSection>
            </TabsContent>
          </Tabs>

          {/* Closing note */}
          <div className="mt-8 border-t border-[var(--hairline)] pt-5">
            <p className="font-sans text-[12.5px] leading-[1.6] text-[var(--muted)]">
              In plain language: <strong className="text-[var(--ink-2)]">we curate news, we don't create it; confidence scores guide reading, they don't guarantee truth; if you don't agree with any of the above, please stop using DNH and delete your account.</strong> Questions? Reach Support below.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================================================
   SUPPORT — Ticket form
   ============================================================ */

// Categories tuned for an editorial intelligence platform
const SUPPORT_CATEGORIES = [
  { value: "general", label: "General question" },
  { value: "correction", label: "Story or source correction" },
  { value: "account", label: "Account & access" },
  { value: "bug", label: "Report a bug" },
  { value: "privacy", label: "Privacy or data request" },
  { value: "press", label: "Press & partnerships" },
  { value: "feedback", label: "Feedback or idea" },
  { value: "other", label: "Other" },
];

export const SupportModal = ({ open, onOpenChange }) => {
  const { user: SIGNED_IN_USER } = useAuth();
  const isSignedIn = !!SIGNED_IN_USER;
  const [category, setCategory] = useState("general");
  const [name, setName] = useState(isSignedIn ? SIGNED_IN_USER.name : "");
  const [email, setEmail] = useState(isSignedIn ? SIGNED_IN_USER.email : "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const resetForm = () => {
    setCategory("general");
    setName(isSignedIn ? SIGNED_IN_USER.name : "");
    setEmail(isSignedIn ? SIGNED_IN_USER.email : "");
    setSubject("");
    setMessage("");
    setTicketId(null);
  };

  const handleClose = (next) => {
    onOpenChange(next);
    if (!next) {
      setTimeout(resetForm, 250);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const BASE = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BASE}/api/support/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim() || `${category} — message from ${name.trim()}`,
          message: message.trim(),
          category,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setTicketId(data.ticket_id);
        toast.success(`Ticket ${data.ticket_id} created.`);
      } else {
        toast.error(data.detail || "Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Network error — please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="support-modal"
        className="bg-[var(--paper)] text-[var(--ink)] border border-[var(--hairline-strong,#D8D2C6)] rounded-[18px] sm:max-w-[640px] max-h-[90vh] overflow-y-auto p-0"
      >
        <DialogHeader className="p-7 sm:p-8 pb-0 text-left">
          <ModalHeader
            eyebrow="Support · We respond within 24 hours"
            title={ticketId ? "Ticket created" : "How can we help?"}
            kicker={
              ticketId
                ? "Your message is in the queue. Our team will reach out at the email below."
                : "Tell us what's going on. The more context you share, the faster we can help."
            }
          />
        </DialogHeader>

        <div className="px-7 sm:px-8 pb-8 mt-6">
          {ticketId ? (
            /* ---------------- Success state ---------------- */
            <div data-testid="support-success" className="text-left">
              <div className="rounded-[12px] border border-[var(--hairline)] bg-[var(--surface-2)] p-5 flex items-start gap-3">
                <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[var(--ink)] text-[var(--paper)] shrink-0">
                  <CheckCircle2 size={16} strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--crimson)]">
                    Ticket reference
                  </div>
                  <div
                    className="font-serif text-[22px] leading-tight text-[var(--ink)] mt-1"
                    data-testid="support-ticket-id"
                  >
                    {ticketId}
                  </div>
                  <div className="font-sans text-[13px] text-[var(--ink-2)] mt-2 leading-[1.55]">
                    A confirmation has been sent to{" "}
                    <strong className="text-[var(--ink)]">{email}</strong>.
                    Please keep this reference for any follow-up.
                  </div>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12.5px] font-mono tracking-[0.04em]">
                <div className="rounded-[10px] border border-[var(--hairline)] p-3">
                  <div className="text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)]">
                    Category
                  </div>
                  <div className="text-[var(--ink)] mt-1">
                    {SUPPORT_CATEGORIES.find((c) => c.value === category)?.label}
                  </div>
                </div>
                <div className="rounded-[10px] border border-[var(--hairline)] p-3">
                  <div className="text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)]">
                    Expected reply
                  </div>
                  <div className="text-[var(--ink)] mt-1">
                    Within 24 hours · weekdays
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-7 flex flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => resetForm()}
                  data-testid="support-new-ticket"
                  className="rounded-full border-[var(--hairline-strong,#D8D2C6)] text-[var(--ink)] hover:bg-[var(--surface-2)]"
                >
                  Submit another
                </Button>
                <Button
                  type="button"
                  onClick={() => handleClose(false)}
                  data-testid="support-done"
                  className="rounded-full bg-[var(--ink)] hover:bg-[var(--ink)]/90 text-[var(--paper)]"
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            /* ---------------- Form state ---------------- */
            <form
              onSubmit={handleSubmit}
              className="text-left"
              data-testid="support-form"
            >
              {/* Signed-in banner */}
              {isSignedIn ? (
                <div className="rounded-[10px] border border-[var(--hairline)] bg-[var(--surface-2)] p-3 mb-5 flex items-center gap-2.5 text-[12.5px]">
                  <Sparkles size={14} strokeWidth={1.75} className="text-[var(--crimson)]" />
                  <span className="text-[var(--ink-2)]">
                    Signed in as{" "}
                    <strong className="text-[var(--ink)]">{name}</strong> ({email})
                  </span>
                </div>
              ) : (
                <div className="rounded-[10px] border border-dashed border-[var(--hairline-strong,#D8D2C6)] p-3 mb-5 flex items-center gap-2.5 text-[12px] font-mono tracking-[0.04em]">
                  <User size={13} strokeWidth={1.75} className="text-[var(--muted)]" />
                  <span className="text-[var(--muted)]">
                    Not signed in — please share your name and email so we can
                    follow up.
                  </span>
                </div>
              )}

              {/* Category */}
              <div className="mb-4">
                <Label
                  htmlFor="support-category"
                  className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)] mb-2 block"
                >
                  What is this about?
                </Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger
                    id="support-category"
                    data-testid="support-category"
                    className="bg-[var(--paper)] border-[var(--hairline-strong,#D8D2C6)] text-[var(--ink)] rounded-[10px] h-11"
                  >
                    <SelectValue placeholder="Choose a category" />
                  </SelectTrigger>
                  <SelectContent
                    className="bg-[var(--paper)] border-[var(--hairline-strong,#D8D2C6)] text-[var(--ink)] rounded-[10px]"
                  >
                    {SUPPORT_CATEGORIES.map((c) => (
                      <SelectItem
                        key={c.value}
                        value={c.value}
                        data-testid={`support-category-option-${c.value}`}
                        className="text-[var(--ink)] focus:bg-[var(--surface-2)] focus:text-[var(--ink)]"
                      >
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Name + email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label
                    htmlFor="support-name"
                    className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)] mb-2 block"
                  >
                    Your name
                  </Label>
                  <Input
                    id="support-name"
                    data-testid="support-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Priya Iyer"
                    disabled={isSignedIn}
                    className="bg-[var(--paper)] border-[var(--hairline-strong,#D8D2C6)] text-[var(--ink)] rounded-[10px] h-11"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="support-email"
                    className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)] mb-2 block"
                  >
                    Email
                  </Label>
                  <Input
                    id="support-email"
                    data-testid="support-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    disabled={isSignedIn}
                    className="bg-[var(--paper)] border-[var(--hairline-strong,#D8D2C6)] text-[var(--ink)] rounded-[10px] h-11"
                  />
                </div>
              </div>

              {/* Subject */}
              <div className="mb-4">
                <Label
                  htmlFor="support-subject"
                  className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)] mb-2 block"
                >
                  Subject <span className="normal-case tracking-normal text-[10px] opacity-70">(optional)</span>
                </Label>
                <Input
                  id="support-subject"
                  data-testid="support-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Short summary of your request"
                  className="bg-[var(--paper)] border-[var(--hairline-strong,#D8D2C6)] text-[var(--ink)] rounded-[10px] h-11"
                />
              </div>

              {/* Message */}
              <div className="mb-2">
                <Label
                  htmlFor="support-message"
                  className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)] mb-2 block"
                >
                  Your message
                </Label>
                <Textarea
                  id="support-message"
                  data-testid="support-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us what's going on — include URLs, brief IDs (e.g. DNH-001234), screenshots if relevant."
                  rows={5}
                  className="bg-[var(--paper)] border-[var(--hairline-strong,#D8D2C6)] text-[var(--ink)] rounded-[10px] min-h-[120px] resize-none"
                />
              </div>

              <div className="flex items-center gap-2 mb-6 text-[11px] font-mono tracking-[0.04em] text-[var(--muted)]">
                <Mail size={12} strokeWidth={1.75} />
                <span>
                  We respond within 24 hours on weekdays. Your data is handled
                  per our Privacy notice.
                </span>
              </div>

              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  data-testid="support-cancel"
                  className="rounded-full border-[var(--hairline-strong,#D8D2C6)] text-[var(--ink)] hover:bg-[var(--surface-2)]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="support-submit"
                  className="rounded-full bg-[var(--crimson)] hover:bg-[var(--crimson-hover)] text-[var(--paper)] inline-flex items-center gap-2"
                >
                  {submitting ? (
                    <>Submitting…</>
                  ) : (
                    <>
                      <Send size={14} strokeWidth={2} />
                      Submit ticket
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
